import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../Components/Layout/Layout';
import { AuthContext } from '../contextStore/AuthContext';
import { Firebase, firebase } from '../firebase/config';
import { getTransactionsForBuyer, getTransactionsForSeller } from '../firebase/collections';
import { formatPrice, formatDate } from '../utils/formatters';
import './Transactions.css';

function TransactionsPage() {
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('buying');

  // Hook up real-time live data streaming listeners
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("LOGGED IN USER UID (Session Context):", user?.uid);

    const baseQuery = tab === 'buying'
      ? getTransactionsForBuyer(user.uid, 50)
      : getTransactionsForSeller(user.uid, 50);

    const unsubscribe = baseQuery.onSnapshot((snap) => {
      const updatedList = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setTransactions(updatedList);
      setLoading(false);
    }, (err) => {
      console.error("Real-time ledger updates engine failed:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid, tab]);

  // --- ESCROW WORKFLOW ACTIONS ---

  // Buyer Action: Updates state directly to 'delivered' so Admin can release escrow manually later
  const handleConfirmDelivery = async (transactionId) => {
    const confirmDelivery = window.confirm(
      "Confirming delivery flags the item as received. An admin will review and release the escrow payout securely."
    );
    if (!confirmDelivery) return;

    try {
      setLoading(true);

      console.log("=== INITIATING LOCAL FIRESTORE STATUS CONVERSION ===");
      console.log("Targeting Transaction Document:", transactionId);

      await Firebase.firestore()
        .collection('transactions')
        .doc(transactionId)
        .update({
          status: 'delivered',
          shippingStatus: 'delivered', // Sync logistics layer status alongside core status
          deliveryConfirmedAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

      console.log("Transaction state conversion successful: marked as delivered.");
      alert("Delivery successfully confirmed! The status has been updated for admin escrow release.");
    } catch (err) {
      console.error("--- LOCAL STATUS UPDATE FAULT TRACE ---", err);
      alert(`Failed to confirm delivery update: \n${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="transactionsPage">
          <p>Please log in to view your transactions.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="transactionsPage">
        <h1>ClubLoop Ledger</h1>

        <div className="transactionsTabs">
          <button
            type="button"
            className={tab === 'buying' ? 'active' : ''}
            onClick={() => setTab('buying')}
          >
            Buying (My Orders)
          </button>
          <button
            type="button"
            className={tab === 'selling' ? 'active' : ''}
            onClick={() => setTab('selling')}
          >
            Selling (My Earnings)
          </button>
        </div>

        {loading ? (
          <p>Loading transaction history...</p>
        ) : transactions.length === 0 ? (
          <p className="transactionsEmpty">No history logged under this profile tab segment.</p>
        ) : (
          <ul className="transactionsList">
            {transactions.map((t) => (
              <li key={t.id} className="transactionsItem">
                <div className="transactionsItemImage">
                  {t.productImage || t.thumbnailUrl ? (
                    <img src={t.productImage || t.thumbnailUrl} alt="" />
                  ) : (
                    <span className="cl-fallback-span">Club</span>
                  )}
                </div>

                <div className="transactionsItemBody">
                  <Link to={`/ad/${t.productId}`} className="transactionsItemTitle">
                    {t.productName || "Golf Equipment"}
                  </Link>

                  <p className="transactionsItemMeta">
                    {tab === 'buying'
                      ? `Seller ID: #${t.sellerId?.slice(0, 8)}`
                      : `Buyer ID: #${t.buyerId?.slice(0, 8)}`}
                  </p>

                  <p className="transactionsItemAmount">
                    {formatPrice(t.totalAmount || t.amount)}
                  </p>

                  <span className={`transactionsItemStatus transactionsItemStatus--${t.status}`}>
                    {t.status === 'escrow_held' ? '🔒 Escrow Held (Paid)' : t.status?.replace('_', ' ')}
                  </span>

                  {t.createdAt?.toDate && (
                    <span className="transactionsItemDate">
                      {formatDate(t.createdAt.toDate())}
                    </span>
                  )}

                  {/* --- DYNAMIC SHIPROCKET TRACKING CARD --- */}
                  {t.shiprocketOrderId && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px 14px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ color: '#0f172a', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          🚚 Logistics Hub Tracking
                        </span>
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {t.shippingStatus?.replace('_', ' ') || 'Processing Pipeline'}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', color: '#475569', fontFamily: 'monospace', fontSize: '12px' }}>
                        <div>Order Reference: <strong style={{color: '#1e293b'}}>{t.shiprocketOrderId}</strong></div>
                        {t.shiprocketShipmentId && (
                          <div>Shipment Ref: <strong style={{color: '#1e293b'}}>{t.shiprocketShipmentId}</strong></div>
                        )}
                      </div>

                      {/* External Tracking Link Check */}
                      {t.awbCode && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                          <a
                            href={`https://shiprocket.co/tracking/${t.awbCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '2px' }}
                          >
                            🔗 Live Waybill Tracking Tracker ({t.awbCode})
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* --- ACTION MANAGEMENT LAYOUT ENGINE --- */}
                  <div className="transactionsItemActions" style={{ marginTop: '14px' }}>

                    {/* INTERACTION CONTEXT A: USER IS BUYER */}
                    {tab === 'buying' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {t.status === 'escrow_held' && !t.shiprocketOrderId && (
                          <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                            ⏳ Syncing automated fulfillment pipeline keys...
                          </span>
                        )}

                        {/* Allow confirmation if backend generated manifest or status shifted explicitly to transit states */}
                        {(t.status === 'shipped' || t.shippingStatus === 'manifest_generated' || t.status === 'escrow_held') && t.status !== 'delivered' && t.status !== 'completed' && !t.payoutReleased && (
                          <button
                            type="button"
                            className="cl-action-btn cl-btn-success"
                            onClick={() => handleConfirmDelivery(t.id)}
                            style={{
                              backgroundColor: '#16a34a',
                              color: '#ffffff',
                              border: 'none',
                              padding: '8px 14px',
                              borderRadius: '6px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Confirm Delivery (Package Inspected)
                          </button>
                        )}
                      </div>
                    )}

                    {/* INTERACTION CONTEXT B: USER IS SELLER */}
                    {tab === 'selling' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {t.status === 'awaiting_payment' && (
                          <span className="cl-info-tag" style={{ color: '#b45309', backgroundColor: '#fffbeb', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                            ⚠️ Awaiting Gateway Ledger Balance Confirmation
                          </span>
                        )}

                        {t.status === 'escrow_held' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span className="cl-info-tag cl-tag-success" style={{ color: '#15803d', backgroundColor: '#f0fdf4', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', width: 'fit-content' }}>
                              ✓ Escrow Capital Secured
                            </span>

                            {t.shiprocketOrderId ? (
                              <div style={{
                                fontSize: '12px',
                                color: '#0f766e',
                                backgroundColor: '#f0fdfa',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontWeight: '500',
                                border: '1px solid #ccfbf1',
                                lineHeight: '1.4'
                              }}>
                                📌 <strong>Fulfillment Auto-Booked:</strong> Shiprocket has dispatched courier routing to your physical registration address. Pack your clubs securely and print your dispatch labels.
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                                ⏳ Provisioning remote manifest layout configurations...
                              </span>
                            )}
                          </div>
                        )}

                        {t.status === 'shipped' && (
                          <span className="cl-info-tag" style={{ color: '#1d4ed8', backgroundColor: '#eff6ff', padding: '6px 12px', borderRadius: '4px', fontSize: '12px' }}>
                            🚚 Package In-Transit — Awaiting Buyer Delivery Receipt Authorization
                          </span>
                        )}
                      </div>
                    )}

                    {/* GLOBAL STATUS LAYOUT TRACKING LABELS */}
                    {t.status === 'delivered' && !t.payoutReleased && (
                      <span className="cl-info-tag cl-tag-warning" style={{ color: '#c2410c', backgroundColor: '#fff7ed', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', display: 'inline-block' }}>
                        {tab === 'selling'
                          ? '🎉 Package Delivered! Escrow clearing split routing under verification checks'
                          : '✓ Receipt Confirmed — Awaiting final backend ledger clearance'}
                      </span>
                    )}

                    {(t.status === 'completed' || t.payoutReleased) && (
                      <span className="cl-info-tag cl-tag-success" style={{ color: '#15803d', backgroundColor: '#f0fdf4', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', display: 'inline-block' }}>
                        {tab === 'selling' ? '✅ Funds Disbursed cleanly to Bank Account' : '✅ Transaction Deal Finalized'}
                      </span>
                    )}
                  </div>

                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

export default TransactionsPage;
